import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {ReconstructedInterventionService} from '../../stores/reconstructed-intervention.service';
import {CropService} from '../../stores/crop.service';

export const INTERVENTION_TYPES = [
  { value: 'SPRAYING', label: 'Pulvérisation' },
  { value: 'SEEDING', label: 'Semis' },
  { value: 'HARVESTING', label: 'Récolte' },
  { value: 'PLOWING', label: 'Labour' },
  { value: 'CULTIVATION', label: 'Cultivation' },
  { value: 'FERTILIZING', label: 'Fertilisation' },
  { value: 'OTHER', label: 'Autre' }
];

export const VALIDATION_ACCURACY_OPTIONS = [
  { value: 'CORRECT', label: 'Données Correctes' },
  { value: 'APPROXIMATELY_CORRECT', label: 'Données Approximativement Correctes' }
];

@Component({
  selector: 'app-validate-dialog',
  templateUrl: './validate-dialog.component.html',
  styleUrls: ['./validate-dialog.component.scss']
})
export class ValidateDialogComponent implements OnInit {
  @Output() onValidate = new EventEmitter<void>();
  
  visible = false;
  interventionId: number;
  validationAccuracy: string = null; // No default value
  interventionType = null; // No default selection
  cultureId: number = null;
  createFields = false; // Toggle for creating fields from work areas
  isEditing = false; // Track if we're editing an existing validation
  
  interventionTypes = INTERVENTION_TYPES;
  validationAccuracyOptions: Array<{value: string, label: string, checked: boolean}> = [];
  cultures: any[] = [];

  constructor(
    private reconstructedInterventionService: ReconstructedInterventionService,
    private cropService: CropService
  ) {
  }

  ngOnInit(): void {
    // Initialize validation accuracy options with checked property
    this.validationAccuracyOptions = VALIDATION_ACCURACY_OPTIONS.map(opt => ({
      ...opt,
      checked: false // No default selection
    }));
    
    // Load cultures list
    this.cropService.loadAll().subscribe({
      next: (response) => {
        this.cultures = response.data || [];
      },
      error: (err) => {
        console.error('Error loading cultures:', err);
      }
    });
  }

  show(interventionId: number, currentData?: {accuracy?: string, type?: string, cultureId?: number}): void {
    this.interventionId = interventionId;
    this.isEditing = !!(currentData && currentData.accuracy); // Edit mode if current data exists
    
    if (currentData) {
      // Pre-fill with existing values
      this.validationAccuracy = currentData.accuracy || null;
      this.interventionType = currentData.type || null;
      this.cultureId = currentData.cultureId || null;
      
      // Update checkboxes
      this.validationAccuracyOptions.forEach(opt => {
        opt.checked = opt.value === currentData.accuracy;
      });
    } else {
      // Reset for new validation
      this.validationAccuracy = null;
      this.validationAccuracyOptions.forEach(opt => opt.checked = false);
      this.interventionType = null;
      this.cultureId = null;
    }
    
    this.createFields = false; // Always reset toggle
    this.visible = true;
  }

  onAccuracyChanged(value: string, checked: boolean): void {
    if (checked) {
      this.validationAccuracy = value;
      // Uncheck other options (radio button behavior)
      this.validationAccuracyOptions.forEach(opt => {
        if (opt.value !== value) {
          opt.checked = false;
        }
      });
    } else {
      // If unchecking, clear the value
      this.validationAccuracy = null;
    }
  }

  close(): void {
    this.visible = false;
  }

  validate(): void {
    if (!this.validationAccuracy || !this.interventionType || !this.cultureId) {
      return;
    }
    
    // If createFields is enabled, call the validate-and-create-fields endpoint
    // Pass validation parameters so user's choices are respected
    if (this.createFields) {
      this.reconstructedInterventionService.validateAndCreateFields(
        this.interventionId,
        this.validationAccuracy,
        this.interventionType,
        this.cultureId
      ).subscribe({
        next: () => {
          this.onValidate.emit();
          this.close();
        },
        error: (err) => {
          console.error('Error validating and creating fields:', err);
          // Show error notification
        }
      });
    } else {
      // Normal validation - all fields are required
      this.reconstructedInterventionService.validate(
        this.interventionId, 
        this.validationAccuracy, 
        this.interventionType,
        this.cultureId
      ).subscribe({
        next: () => {
          this.onValidate.emit();
          this.close();
        },
        error: (err) => {
          console.error('Error validating intervention:', err);
          // Show error notification
        }
      });
    }
  }
}

